/**
 * Аккаунт: профиль, привязка брокера, депозит/вывод, настройки автоторговли,
 * рефералы. Визуал по design-spec §7 (Profile): hero → broker activation →
 * referral → utility. Наполнение — BOT_SPEC ШАГ 11–18, 21.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, apiMessage } from '../lib/api.js';
import { strategyName } from '../lib/strategy.js';
import type { ReferralsPayload, SettingsPayload, StrategyPreset } from '../lib/api.js';
import { haptic, openLink, openTelegramLink } from '../lib/telegram.js';
import { money } from '../lib/format.js';
import { useCountUp } from '../lib/useCountUp.js';
import { useApp } from '../state/app.js';
import { SheetPortal } from '../components/SheetPortal.js';
import { InfoLinks, InfoSheet } from './InfoSheets.js';
import type { SheetId } from './InfoSheets.js';
import type { AppConfig } from '../lib/api.js';

export function AccountScreen({
  config,
}: {
  config: AppConfig | null;
}) {
  const { me, refresh, showToast, art, t, openWallet } = useApp();
  const [sheet, setSheet] = useState<SheetId>(null);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [referrals, setReferrals] = useState<ReferralsPayload | null>(null);
  const [email, setEmail] = useState('');
  /**
   * Вход по коду из письма: e-mail → шесть цифр. Паролей нет, поэтому нет и
   * развилки «создать / войти» — нового пользователя брокер заводит сам, под нами.
   */
  const [code, setCode] = useState('');
  const [codeStep, setCodeStep] = useState(false);
  // секунд до повторной отправки: брокер схлопывает повторы чаще раза в 30 с
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  // выход из аккаунта: первый тап «взводит» кнопку, второй выполняет
  const [logoutArmed, setLogoutArmed] = useState(false);
  // тап по стратегии открывает шторку с описанием (тексты — из бота)
  const [strategySheet, setStrategySheet] = useState<StrategyPreset | null>(null);
  // «докручивающийся» реальный баланс
  const realBalance = useCountUp(me?.balances.real ?? 0);

  useEffect(() => {
    void api.settings().then(setSettings).catch(() => undefined);
    void api.referrals().then(setReferrals).catch(() => undefined);
  }, [me?.broker.linked]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((value) => value - 1), 1_000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  async function sendCode() {
    haptic('medium');
    setBusy(true);
    try {
      const result = await api.requestLoginCode(email);
      if (result.status === 'sent') {
        setCodeStep(true);
        setCode('');
        setResendIn(result.resendInSeconds);
        showToast(t('app.account.code_sent', { email: email.trim().toLowerCase() }));
      } else if (result.status === 'rate_limited') {
        // код уже мог уйти прошлым запросом — пускаем к вводу, а не держим на e-mail
        setCodeStep(true);
        setResendIn(result.retryInSeconds);
        showToast(t('app.account.code_wait', { seconds: result.retryInSeconds }));
      } else {
        showToast(t('app.account.email_invalid'));
      }
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.link_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(value: string) {
    setBusy(true);
    try {
      const result = await api.verifyLoginCode(email, value);
      if (result.status === 'linked') {
        haptic('medium');
        showToast(t(result.isNew ? 'app.account.created' : 'app.account.connected'));
        setCodeStep(false);
        setCode('');
        await refresh();
      } else if (result.status === 'rate_limited') {
        showToast(t('app.account.code_wait', { seconds: result.retryInSeconds }));
      } else if (result.status === 'blocked') {
        showToast(t('app.account.blocked'));
      } else {
        setCode('');
        showToast(t('app.account.code_invalid'));
      }
    } catch (err) {
      // код верный, но счёт уже привязан к другому Telegram — 409 от сервера
      showToast(
        err instanceof ApiError && err.isConflict
          ? t('app.account.conflict')
          : apiMessage(err, t, 'app.account.link_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * Вход на сайте брокера (OAuth): открываем внешний браузер, а сами опрашиваем
   * профиль — callback приходит на сервер, прила узнаёт о привязке по `me`.
   */
  const [oauthPending, setOauthPending] = useState(false);

  useEffect(() => {
    if (!oauthPending || me?.broker.linked) {
      if (oauthPending && me?.broker.linked) setOauthPending(false);
      return;
    }
    const timer = setInterval(() => void refresh(), 3_000);
    // state живёт 10 минут — дольше ждать нечего
    const stop = setTimeout(() => setOauthPending(false), 10 * 60_000);
    return () => {
      clearInterval(timer);
      clearTimeout(stop);
    };
  }, [oauthPending, me?.broker.linked, refresh]);

  async function signInOnSite() {
    haptic('medium');
    try {
      const { url } = await api.oauthUrl();
      if (!url) {
        showToast(t('app.account.link_failed'));
        return;
      }
      openLink(url);
      setOauthPending(true);
      showToast(t('app.account.oauth_wait'));
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.link_failed'));
    }
  }

  function onCodeInput(raw: string) {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    // шестая цифра — сразу проверяем: лишний тап по кнопке на телефоне ни к чему
    if (digits.length === 6 && !busy) void verifyCode(digits);
  }

  useEffect(() => {
    if (!logoutArmed) return;
    const timer = setTimeout(() => setLogoutArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [logoutArmed]);

  async function logoutAccount() {
    haptic('medium');
    if (!logoutArmed) {
      setLogoutArmed(true);
      return;
    }

    setLogoutArmed(false);
    setBusy(true);
    try {
      await api.unlinkAccount();
      // поля формы очищаем: дальше сюда можно ввести другую почту
      setEmail('');
      setCode('');
      setCodeStep(false);
      showToast(t('app.account.logged_out'));
      await refresh();
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.save_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleMode() {
    haptic('medium');
    const goingReal = me?.user.mode !== 'REAL';
    setBusy(true);
    try {
      await api.setMode(goingReal ? 'REAL' : 'DEMO');
      await refresh();
      showToast(t(goingReal ? 'app.account.real_on' : 'app.account.demo_on'));
    } catch (err) {
      // известные коды (депозит, чужой аккаунт) переводятся словарём
      showToast(apiMessage(err, t, 'app.account.save_failed'));
    } finally {
      setBusy(false);
    }
  }

  /** Пополнение и вывод — в виджете брокера (шторка кошелька); кабинет брокера — внешней ссылкой. */
  async function openBrokerLink(kind: 'deposit' | 'withdraw' | 'platform') {
    haptic('medium');
    if (kind !== 'platform') {
      openWallet({ mode: kind, stage: 'account' });
      return;
    }
    try {
      const { link } = await api.platformLink();
      openLink(link);
    } catch (err) {
      showToast(apiMessage(err, t, 'app.tokens.broker_silent'));
    }
  }

  async function patchSettings(patch: Parameters<typeof api.updateSettings>[0], toast: string) {
    haptic();
    try {
      await api.updateSettings(patch);
      setSettings(await api.settings());
      await refresh();
      showToast(toast);
    } catch (err) {
      // в REAL стратегия «от $X» требует реального баланса — сумма важнее
      // безликого «не сохранилось»
      const minBalance =
        err instanceof ApiError
          ? (err.payload as { strategyMinBalance?: number } | null)?.strategyMinBalance
          : undefined;
      showToast(
        typeof minBalance === 'number'
          ? t('app.account.strategy_locked', { amount: minBalance })
          : t('app.account.save_failed'),
      );
    }
  }

  if (!me) return null;

  return (
    <div className="screen">
      {/* профиль и балансы */}
      <section className="card card-lg">
        <div className="row-between">
          <div>
            <span className="kicker">{t('app.nav.account')}</span>
            <div className="profile-name">{me.user.email ?? t('app.account.no_link')}</div>
          </div>
          <span className={`pill ${me.user.mode === 'REAL' ? 'pill-green' : 'pill-pink'}`}>
            {me.user.mode}
          </span>
        </div>

        {/* trader_id — то, по чему поддержка и партнёрка находят человека
            у брокера; без него на вопрос «какой у меня счёт» ответить нечем */}
        {me.broker.traderId && (
          <div className="row-between">
            <span className="label">{t('app.account.trader_id')}</span>
            <button
              type="button"
              className="pill"
              onClick={() => {
                void navigator.clipboard?.writeText(me.broker.traderId ?? '');
                showToast(t('app.account.trader_id_copied'));
              }}
            >
              {me.broker.traderId}
            </button>
          </div>
        )}

        {/* счёт живёт на настоящей платформе — даём открыть кабинет брокера
            (просьба заказчика 2026-08-21); ссылка через /api/go/platform */}
        {me.broker.linked && (
          <div className="row-between">
            <span className="label">{t('app.account.platform')}</span>
            <button
              type="button"
              className="pill"
              onClick={() => void openBrokerLink('platform')}
            >
              {t('app.account.platform_open')} ↗
            </button>
          </div>
        )}

        <div className="label">{t('app.account.balance')}</div>
        <div className="profile-balance">
          <span className="profile-balance-sign">$</span>
          {money(realBalance)}
          <span className="profile-balance-currency">{me.balances.currency}</span>
        </div>
        <div className="body">
          {t('app.account.demo_line', { demo: money(me.balances.demo), tokens: me.user.tokens })}
        </div>

        <div className="row">
          <button
            type="button"
            className="cta grow"
            disabled={!me.broker.linked}
            onClick={() => void openBrokerLink('deposit')}
          >
            {t('app.home.deposit')}
          </button>
          <button
            type="button"
            className="cta cta-secondary grow"
            disabled={!me.broker.linked}
            onClick={() => void openBrokerLink('withdraw')}
          >
            {t('app.account.withdraw')}
          </button>
        </div>

        {/* второй ряд действий — те же половины, что Deposit/Withdraw выше
            (аудит UI 2026-08-21, A1): раньше кнопки шли по ширине текста.
            «Real» ведёт через гейт (депозит/чужой аккаунт), «выход» — с
            двухшаговым подтверждением; сам аккаунт у брокера остаётся */}
        {me.broker.linked && (
          <div className="row">
            <button
              type="button"
              className="cta cta-secondary cta-sm grow"
              disabled={busy}
              onClick={() => void toggleMode()}
            >
              {t(me.user.mode === 'REAL' ? 'app.account.go_demo' : 'app.account.go_real')}
            </button>
            <button
              type="button"
              className="cta cta-secondary cta-sm grow"
              disabled={busy}
              onClick={() => void logoutAccount()}
            >
              {logoutArmed ? t('app.account.logout_confirm') : t('app.account.logout')}
            </button>
          </div>
        )}
      </section>

      {/* привязка брокера */}
      {!me.broker.linked && (
        <section className="card card-lg card-violet">
          <img className="broker-art art-3d art-3d-slow" src={art('art.broker', '/art/broker.svg')} alt="" width={132} height={132} />
          <span className="kicker">{t('app.account.activation')}</span>
          <h2 className="card-title">{t('app.account.connect_title')}</h2>
          <p className="body">
            {t('app.account.connect_text')}
          </p>
          {!codeStep ? (
            <>
              <input
                className="field"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@mail.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
              <button
                type="button"
                className="cta cta-block"
                disabled={busy || email.length < 5}
                onClick={() => void sendCode()}
              >
                {busy ? t('app.account.checking') : t('app.account.send_code')}
              </button>
              {config?.brokerOAuth && (
                <button type="button" className="chip" style={{ marginTop: 10, width: '100%' }} onClick={() => void signInOnSite()}>
                  <span className="chip-label">
                    {oauthPending ? t('app.account.oauth_wait') : t('app.account.oauth')}
                  </span>
                </button>
              )}
            </>
          ) : (
            <>
              <p className="body">{t('app.account.code_hint', { email: email.trim().toLowerCase() })}</p>
              <input
                className="field field-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                placeholder="• • • • • •"
                value={code}
                autoFocus
                onChange={(event) => onCodeInput(event.target.value)}
              />
              <button
                type="button"
                className="cta cta-block"
                disabled={busy || code.length !== 6}
                onClick={() => void verifyCode(code)}
              >
                {busy ? t('app.account.checking') : t('app.account.connect')}
              </button>
              <div className="chip-grid" style={{ ['--chip-cols' as string]: 2, marginTop: 10 }}>
                <button
                  type="button"
                  className="chip"
                  disabled={busy || resendIn > 0}
                  onClick={() => void sendCode()}
                >
                  <span className="chip-label">
                    {resendIn > 0
                      ? t('app.account.code_resend_in', { seconds: resendIn })
                      : t('app.account.code_resend')}
                  </span>
                </button>
                <button
                  type="button"
                  className="chip"
                  disabled={busy}
                  onClick={() => {
                    setCodeStep(false);
                    setCode('');
                  }}
                >
                  <span className="chip-label">{t('app.account.change_email')}</span>
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* настройки автоторговли */}
      {settings && (
        <section className="card card-lg">
          <span className="kicker">{t('app.account.autotrade')}</span>
          <h2 className="card-title">{t('app.account.strategy')}</h2>

          <div className="chip-grid">
            {settings.options.strategies
              .filter((preset) => !preset.manual)
              .map((preset) => {
                // в REAL порог «от $X» обязателен: реальный мартингейл с меньшим
                // балансом оборвётся на середине лестницы (сервер тоже проверяет)
                const locked =
                  me.user.mode === 'REAL' &&
                  preset.minBalanceUsd !== null &&
                  me.balances.real < preset.minBalanceUsd;

                // без эмодзи и с витринным именем; тап открывает шторку с
                // полным описанием — применение только после подтверждения
                return (
                  <button
                    key={preset.id}
                    type="button"
                    disabled={locked}
                    style={locked ? { opacity: 0.45 } : undefined}
                    className={`chip chip-strategy ${settings.current.strategy === preset.id ? 'is-active' : ''}`}
                    onClick={() => {
                      haptic();
                      setStrategySheet(preset);
                    }}
                  >
                    <span className="chip-label">
                      {strategyName(preset, t)}
                      {locked ? ` · $${preset.minBalanceUsd}+` : ''}
                    </span>
                  </button>
                );
              })}
          </div>

          <div className="label">{t('app.account.expiration')}</div>
          <div className="chip-grid" style={{ '--chip-cols': 3 } as React.CSSProperties}>
            {settings.options.expirations.map((seconds) => (
              <button
                key={seconds}
                type="button"
                className={`chip ${settings.current.expirationSeconds === seconds ? 'is-active' : ''}`}
                onClick={() =>
                  void patchSettings({ expirationSeconds: seconds }, t('app.account.expiration_set', { seconds }))
                }
              >
                {t('app.account.seconds', { seconds })}
              </button>
            ))}
          </div>

          <div className="row-between">
            <div>
              <div className="label">{t('app.account.mode')}</div>
              <div className="body">
                {settings.current.autotrade ? t('app.account.mode_auto') : t('app.account.mode_manual')}
              </div>
            </div>
            <button
              type="button"
              className="cta cta-secondary cta-sm"
              onClick={() =>
                void patchSettings(
                  { autotrade: !settings.current.autotrade },
                  settings.current.autotrade
                    ? t('app.account.mode_manual_on')
                    : t('app.account.mode_auto_on'),
                )
              }
            >
              {t('app.account.switch')}
            </button>
          </div>
        </section>
      )}

      {/* рефералы */}
      {referrals && (
        <section className="card card-lg card-tinted">
          <img className="referral-art art-3d art-3d-slow" src={art('art.referral', '/art/referral.svg')} alt="" loading="lazy" />
          <span className="kicker">{t('app.account.referral')}</span>
          <h2 className="card-title">{t('app.account.referral_title')}</h2>

          <div className="stat-grid">
            <div className="stat-tile">
              {/* именно total: друг, начавший торговать, меняет статус и
                  из счётчика invited пропал бы */}
              <div className="stat-value">{referrals.stats.total}</div>
              <div className="stat-label">{t('app.account.invited')}</div>
            </div>
            <div className="stat-tile" style={{ '--stat-color': 'var(--cyan)' } as React.CSSProperties}>
              <div className="stat-value">
                {referrals.stats.confirmed}
              </div>
              <div className="stat-label">{t('app.account.confirmed')}</div>
            </div>
            <div className="stat-tile" style={{ '--stat-color': 'var(--iris)' } as React.CSSProperties}>
              <div className="stat-value">
                {referrals.stats.tokensEarned}
              </div>
              <div className="stat-label">{t('app.home.stat_tokens')}</div>
            </div>
          </div>

          <div className="referral-link">{referrals.link}</div>
          <button
            type="button"
            className="cta cta-block"
            onClick={() => {
              void navigator.clipboard?.writeText(referrals.link);
              showToast(t('app.account.link_copied'));
            }}
          >
            {t('app.account.copy_link')}
          </button>
        </section>
      )}

      <InfoLinks onOpen={setSheet} />

      {/* текст «напишите @…» убран по просьбе заказчика 2026-08-22 — осталась
          только кнопка; чат открывается поверх прилы (openTelegramLink) */}
      {config?.links?.support && (
        <section className="card">
          <span className="label">{t('app.account.support')}</span>
          <button
            type="button"
            className="cta cta-block"
            onClick={() => {
              haptic();
              openTelegramLink(config.links!.support!);
            }}
          >
            {t('app.community.support')}
          </button>
        </section>
      )}

      <InfoSheet id={sheet} config={config} onClose={() => setSheet(null)} />

      {/* описание стратегии перед применением — тот же текст, что в боте */}
      {strategySheet && (
        <SheetPortal>
        <div
          className="sheet-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setStrategySheet(null);
          }}
        >
          <div className="sheet">
            <div className="sheet-handle" />
            <span className="kicker">{t('app.account.autotrade')}</span>
            <h2 className="strategy-sheet-title">{strategyName(strategySheet, t)}</h2>
            {/* механика серии: чем многосделочные режимы отличаются от классики */}
            <p className="body">{t('app.strategy.mode.' + (strategySheet.mode ?? 'recovery'))}</p>

            {/* уровень риска = позиция пресета в линейке, от PULSE к APEX; у автопилота и кастома лестницы нет */}
            {strategySheet.mode !== 'auto' && strategySheet.mode !== 'custom' && (() => {
              const presets = (settings?.options.strategies ?? []).filter((p) => !p.manual);
              const level = presets.findIndex((p) => p.id === strategySheet.id) + 1;
              return (
                <div className="risk-meter">
                  <span className="risk-label">{t('app.strategy.risk')}</span>
                  <div className="risk-meter-cells">
                    {presets.map((p, index) => (
                      <span key={p.id} className={`risk-cell ${index < level ? 'is-on' : ''}`} />
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="strategy-specs" style={strategySheet.mode === 'auto' || strategySheet.mode === 'custom' ? { display: 'none' } : undefined}>
              <div className="stat-tile">
                <div className="stat-value">×{strategySheet.progression}</div>
                <div className="stat-label">{t('app.strategy.progression')}</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">{strategySheet.maxSteps}</div>
                <div className="stat-label">{t('app.strategy.steps')}</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">{strategySheet.maxTrades}</div>
                <div className="stat-label">{t('app.strategy.trades')}</div>
              </div>
              <div className="stat-tile" style={{ '--stat-color': 'var(--iris)' } as React.CSSProperties}>
                <div className="stat-value">
                  {strategySheet.minBalanceUsd === null ? '—' : `$${strategySheet.minBalanceUsd}`}
                </div>
                <div className="stat-label">{t('app.strategy.min_balance')}</div>
              </div>
            </div>

            <div className="row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="cta grow"
                onClick={() => {
                  const preset = strategySheet;
                  setStrategySheet(null);
                  void patchSettings(
                    { strategy: preset.id },
                    t('app.account.strategy_set', { strategy: strategyName(preset, t) }),
                  );
                }}
              >
                {t('app.strategy.apply')}
              </button>
              <button
                type="button"
                className="cta cta-secondary grow"
                onClick={() => setStrategySheet(null)}
              >
                {t('app.common.back')}
              </button>
            </div>
          </div>
        </div>
        </SheetPortal>
      )}
    </div>
  );
}
